import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fund from '@/models/Fund';
import searchIcon from '@/assets/icons/search.svg';
import trashIcon from '@/assets/icons/trash.svg';
import searchStyles from '@/components/ui/SearchInput/SearchInput.module.css';
import styles from './UserFundsField.module.css';

const OPTION_HEIGHT = 44;
const MAX_LIST_HEIGHT = 264;

interface UserFundsFieldProps {
  fundIds: number[];
  onChange: (fundIds: number[]) => void;
  funds?: Fund[];
}

/** The funds a user may view: the current list on top, and a search box to add another one. */
const UserFundsField = ({ fundIds, onChange, funds }: UserFundsFieldProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const inputBoxRef = useRef<HTMLDivElement>(null);
  // State and not a ref: the popover mounts its content after this render, and the virtualizer
  // only measures once it re-renders with the scroll element actually attached. With a plain ref
  // the list stayed empty until some other change (typing) forced a render.
  const [listEl, setListEl] = useState<HTMLDivElement | null>(null);

  const assignedFunds = useMemo(() => {
    const namesById = new Map((funds ?? []).map((fund) => [fund.id, fund.name]));
    return fundIds.map((id) => ({ id, name: namesById.get(id) ?? '' }));
  }, [fundIds, funds]);

  const suggestions = useMemo(() => {
    const assigned = new Set(fundIds);
    const trimmed = query.trim().toLowerCase();
    return (funds ?? [])
      .filter((fund) => fund.id !== -1 && !assigned.has(fund.id))
      .filter((fund) => !trimmed || `${fund.id} ${fund.name}`.toLowerCase().includes(trimmed));
  }, [funds, fundIds, query]);

  const virtualizer = useVirtualizer({
    count: suggestions.length,
    getScrollElement: () => listEl,
    estimateSize: () => OPTION_HEIGHT,
    overscan: 5,
  });

  const addFund = (fundId: number) => {
    onChange([...fundIds, fundId]);
    // Reset the box so the next fund can be picked right away.
    setQuery('');
    setIsPickerOpen(false);
  };

  const removeFund = (fundId: number) => onChange(fundIds.filter((id) => id !== fundId));

  return (
    <div className={styles.field}>
      <span className="body-sm-semibold text-(--font-secondary)">{t('usersManagement.permissions.fundsLabel')}</span>

      {assignedFunds.length === 0 ? (
        <div className={styles.list}>
          <p className={styles.empty}>{t('usersManagement.permissions.noFunds')}</p>
        </div>
      ) : (
        <div className={`${styles.list} custom-scrollbar`}>
          {assignedFunds.map((fund) => (
            <div key={fund.id} className={styles.row}>
              <span className={styles.rowId}>{fund.id}</span>
              <span className={styles.rowName} title={fund.name}>
                {fund.name || t('usersManagement.blade.unknownFund')}
              </span>
              <button
                type="button"
                className={styles.removeButton}
                title={t('usersManagement.permissions.removeFund')}
                aria-label={t('usersManagement.permissions.removeFund')}
                onClick={() => removeFund(fund.id)}
              >
                <span aria-hidden="true" className={styles.trashIcon} style={{ maskImage: `url("${trashIcon}")`, WebkitMaskImage: `url("${trashIcon}")` }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* The list is portalled so it floats over the popup instead of growing it - the dialog card
          has a transform and hidden overflow, which would clip a dropdown rendered in place. */}
      <Popover.Root open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <Popover.Anchor asChild>
          <div className={searchStyles['search-container']} ref={inputBoxRef}>
            <img src={searchIcon} alt="" />
            <input
              type="text"
              value={query}
              onFocus={() => setIsPickerOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsPickerOpen(true);
              }}
              placeholder={t('usersManagement.permissions.addFund')}
              className={`${searchStyles['search-input']} body-md-regular text-(--text-body-1)`}
            />
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className={styles.suggestions}
            // Keep the caret in the search box instead of moving focus into the list
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            // Typing in the anchored input counts as "outside"; that must not close the list
            onInteractOutside={(e) => {
              if (inputBoxRef.current?.contains(e.target as Node)) e.preventDefault();
            }}
          >
            {!funds ? (
              <p className={styles.noResults}>{t('loadingFunds')}...</p>
            ) : suggestions.length === 0 ? (
              <p className={styles.noResults}>{query.trim() ? t('noFundsFound') : t('usersManagement.permissions.allFundsAssigned')}</p>
            ) : (
              <div
                ref={setListEl}
                className={`${styles.suggestionsList} custom-scrollbar`}
                style={{ height: Math.min(suggestions.length * OPTION_HEIGHT, MAX_LIST_HEIGHT) }}
              >
                <div className={styles.suggestionsContent} style={{ height: virtualizer.getTotalSize() }}>
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const fund = suggestions[virtualRow.index];
                    const isLast = virtualRow.index === suggestions.length - 1;
                    return (
                      <div key={fund.id} className={styles.suggestionItem} style={{ transform: `translateY(${virtualRow.start}px)`, height: virtualRow.size }}>
                        <button type="button" className={`${styles.option} ${isLast ? styles.optionLast : ''}`} onClick={() => addFund(fund.id)}>
                          <span className={styles.optionId}>{fund.id}</span>
                          <span className={styles.optionDivider} />
                          <span className={styles.optionName}>{fund.name}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default UserFundsField;
